import { attach, createEffect, createEvent, createStore, sample } from 'effector'

import { logger } from '@portal/logger'

import { env } from '@/shared/config'
import { assertUnreachable } from '@/shared/lib/types'

import {
    type ChannelEvent,
    ChannelEventSchema,
    type ChannelSlowmode,
    type ChatSettings,
    SystemEventSchema,
    type UserMute,
} from '../api'
import { type ConnectedContext, type DisconnectedContext, type ICentrifuge, newCentrifuge } from '../centrifuge'
import { isMessagePublishEvent } from './lib'
import type { ChannelItem } from './types'

export const $centrifuge = createStore<ICentrifuge | null>(null)
export const $chatItems = createStore<Record<string, ChannelItem[]>>({})

export const chatEventReceived = createEvent<{ channel: string; event: ChannelEvent }>()
export const chatMessageReceived = chatEventReceived.filter({ fn: isMessagePublishEvent })

export const channelSubscribed = createEvent<{ channel: string }>()
export const channelUnsubscribed = createEvent<{ channel: string }>()

export const centrifugeConnectFx = createEffect<{ token: string }, ICentrifuge>()
export const centrifugeDisconnectFx = attach({
    source: $centrifuge,
    effect: (centrifuge) => centrifuge?.disconnect(),
})

export const centrifugeConnected = createEvent<ConnectedContext>()
export const centrifugeDisconnected = createEvent<DisconnectedContext>()

export const userMuted = createEvent<UserMute>()
export const userUnmuted = createEvent<{ user_id: string }>()

export const messageHiddenAll = createEvent<{ user_id: string }>()

export const slowmodeUpdated = createEvent<ChannelSlowmode>()

export const chatSettingsUpdated = createEvent<ChatSettings>()

centrifugeConnectFx.use(({ token }) => {
    const centrifuge = newCentrifuge(env.CHAT_CENTRIFUGO_WEBSOCKET_URL, { token })

    centrifuge.on('connected', centrifugeConnected)
    centrifuge.on('disconnected', centrifugeDisconnected)
    centrifuge.on('publication', (publication) => {
        const channel = publication.channel

        logger.info(`server publication in channel '${channel}'`, JSON.stringify(publication, null, '  '))

        // handle system events
        if (channel.startsWith('system:')) {
            try {
                const event = SystemEventSchema.parse(publication.data)
                switch (event.type) {
                    case 'user_muted':
                        userMuted(event.payload)
                        break
                    case 'user_unmuted':
                        userUnmuted(event.payload)
                        break
                    case 'message_hidden_all':
                        messageHiddenAll(event.payload)
                        break
                    case 'channel_slowmode':
                        slowmodeUpdated(event.payload)
                        break
                    case 'chat_settings_updated':
                        chatSettingsUpdated(event.payload)
                        break
                    default:
                        assertUnreachable(event)
                }
            } catch (err) {
                logger.error('got invalid system publication', publication.data, err)
            }

            return
        }

        // handle channel events
        const result = ChannelEventSchema.safeParse(publication.data)
        if (result.success) {
            const message = result.data
            logger.debug(`got new message in a ${channel} channel`, message)
            chatEventReceived({ channel, event: result.data })
        } else {
            logger.error(`got invalid publication`, publication.data, result.error.message)
        }
    })

    centrifuge.on('subscribed', (ctx) => {
        // NOTE: system channels are used for system events and handled separately
        if (ctx.channel.startsWith('system:')) {
            logger.debug(`subscribed to system channel '${ctx.channel}'`)
            return
        }

        logger.info(`subscribed to channel '${ctx.channel}'`)

        channelSubscribed({ channel: ctx.channel })
    })

    centrifuge.on('unsubscribed', (ctx) => {
        // NOTE: system channels are used for system events and handled separately
        if (ctx.channel.startsWith('system:')) {
            logger.debug(`unsubscribed from system channel '${ctx.channel}'`)
            return
        }

        logger.info(`unsubscribed from channel '${ctx.channel}'`)

        channelUnsubscribed({ channel: ctx.channel })
    })

    // log all centrifuge events
    centrifuge
        .on('state', (...args) => logger.debug('centrifuge.on(state):', ...args))
        .on('connecting', (...args) => logger.debug('centrifuge.on(connecting):', ...args))
        .on('connected', (...args) => logger.debug('centrifuge.on(connected):', ...args))
        .on('disconnected', (...args) => logger.debug('centrifuge.on(disconnected):', ...args))
        .on('message', (...args) => logger.debug('centrifuge.on(message):', ...args))
        .on('error', (...args) => logger.debug('centrifuge.on(error):', ...args))
        .on('subscribed', (...args) => logger.debug('centrifuge.on(subscribed):', ...args))
        .on('subscribing', (...args) => logger.debug('centrifuge.on(subscribing):', ...args))
        .on('unsubscribed', (...args) => logger.debug('centrifuge.on(unsubscribed):', ...args))
        .on('publication', (...args) => logger.debug('centrifuge.on(publication):', ...args))
        .on('join', (...args) => logger.debug('centrifuge.on(join):', ...args))
        .on('leave', (...args) => logger.debug('centrifuge.on(leave):', ...args))

    centrifuge.connect()

    return centrifuge
})

sample({
    clock: centrifugeConnectFx.doneData,
    target: $centrifuge,
})
